import React, { useState, useEffect, useRef } from 'react';
import { SplideProps } from '@splidejs/react-splide';

import Actor from './Actor';
import { FrontImageSlider } from '../../utils/types/front-image-slider';
import { gettext } from '../../utils/text';
import AccessibleCarousel, {
    AccessibleCarouselSlide,
} from '../AccessibleCarousel';

interface ActorsCarouselProps {
    artists?: FrontImageSlider[];
}

const carouselOptions: SplideProps['options'] = {
    type: 'loop',
    pagination: false,
    arrows: true,
    perMove: 1,
    gap: '0',
    autoWidth: true,
    autoplay: true,
    interval: 2000,
    speed: 500,
    pauseOnHover: true,
    pauseOnFocus: true,
};

const ActorsCarousel: React.FC<ActorsCarouselProps> = ({ artists }) => {
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const [actors, setActors] = useState<FrontImageSlider[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setActors(artists || []);
    }, [artists]);

    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const totalActors = actors.length;
    const performersLabel = gettext('Opera performers');

    const width = totalActors * 360;
    const shouldUseCarousel =
        totalActors > 0 && (containerWidth === null || containerWidth <= width);

    const slides: AccessibleCarouselSlide[] = actors.map((actor, index) => ({
        id: `${actor.name}-${index}`,
        content: <Actor {...actor} />,
        className: 'gallery-carousel__slide',
        ariaLabel: `${actor.name}, ${actor.role}`,
    }));

    if (!totalActors) {
        return null;
    }

    if (!shouldUseCarousel) {
        return (
            <div
                ref={containerRef}
                className="gallery-carousel__list"
                style={{ width, margin: '0 auto', display: 'flex' }}
                role="list"
                aria-label={performersLabel}
            >
                {actors.map((item, index) => (
                    <div key={index} role="listitem">
                        <Actor {...item} />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div ref={containerRef}>
            <AccessibleCarousel
                slides={slides}
                ariaLabel={performersLabel}
                className="gallery-carousel__splide"
                showDots={false}
                splideOptions={carouselOptions}
                ariaAnnouncements={false}
            />
        </div>
    );
};

export default ActorsCarousel;
